import { environment } from "../../environments/environment";
import {
  appendHttpParams,
  createHttpParams,
  resolveApiVersion,
  selectApiUrl,
} from "./api-version";

const BASE_URL = environment.baseUrl;
const EXPURL = environment.expurl;
const USER_BASE_URL = `${environment.phoBaseUrl.replace(/\/v1\/?$/, "")}/`;
const GO_PROCESS_URL = environment.goProcess;
const PHOBASEURL = environment.phoBaseUrl;
const PHOBASEURL_V2 = environment.phoBaseUrlV2;
const PHOBASEURL_V3 = environment.phoBaseUrlV3;
const GOPROCESSSTATES = environment.goProcessStates;
const PHO_TASK_STREAM_URL = environment.phoTaskStreamUrl;
const PHO_BASE = PHOBASEURL.replace(/\/v\d+$/, "");
const PHO_VERSION_URLS = {
  V1: PHOBASEURL,
  V2: PHOBASEURL_V2,
  V3: PHOBASEURL_V3,
} as const;

const phoVersionUrl = (version: string, fallback: "V1" | "V2" | "V3" = "V3") =>
  selectApiUrl(resolveApiVersion(version, fallback), PHO_VERSION_URLS);
const currentPhoUrl = (isOlderExperiment: boolean) =>
  phoVersionUrl(isOlderExperiment ? "V1" : "V2");

export const API_ENDPOINTS = {
  feedback: {
    dashboard: `${PHO_BASE}/feedback/dashboard`,
    download: `${PHO_BASE}/feedback/download`,
    metadata: `${PHO_BASE}/feedback/metadata`,
    submitted: (period: string) =>
      `${PHO_BASE}/feedback/${encodeURIComponent(period)}/submitted`,
    submit: `${PHO_BASE}/feedback`,
  },
  user: {
    me: `${USER_BASE_URL}me`,
    meV3: `${USER_BASE_URL}users/me`,
    login: `${PHO_BASE}/auth/login`,
    users: `${USER_BASE_URL}users/`,
    acceptTerms: `${USER_BASE_URL}/user/termscondition`,
    usersByID: `${BASE_URL}users/`,
    getUser: `${USER_BASE_URL}user/search`,
    searchUserV3: `${USER_BASE_URL}users/search`,
    logout: `${USER_BASE_URL}auth/logout`,
    refreshToken: `${USER_BASE_URL}auth/refresh_access_token`,
    newsletter: `${USER_BASE_URL}newsletter`,
    approval: `${USER_BASE_URL}user/approval`,
    approvalV3: `${USER_BASE_URL}users/onboard`,
    sharedWith: `${PHO_BASE}/auth/bulk/users/detail`,
    countriesList: `${USER_BASE_URL}countries`,
    countriesListWithZone: `${USER_BASE_URL}countries/zone/list`,
    myTickets: `${USER_BASE_URL}tickets/my-tickets`,
  },
  copilot: {
    config: `${PHO_BASE}/copilot/config`,
    conversationHistory: `${PHO_BASE}/copilot/conversation-history`,
    conversationHistoryByExperiment: (expId: string) =>
      `${PHO_BASE}/copilot/${expId}/conversation-history`,
    downloadExperimentPpt: (expId: string) =>
      `${PHO_BASE}/copilot/${expId}/download_experiment_ppt`,
    downloadDashboardPpt: (expId: string) =>
      `${PHO_BASE}/copilot/${expId}/download_dashboard_ppt`,
  },
  roles: {
    permission_mappings: `${USER_BASE_URL}roles/permissions/mapping`,
  },
  projects: {
    projects: `${BASE_URL}projects`,
    keyaccounts: `${BASE_URL}key_account_by_country`,
    regions: `${BASE_URL}regions_by_key_account_and_country`,
    createkeyaccounts: `${BASE_URL}key_accounts`,
    createregions: `${BASE_URL}regions`,
    zone: `${BASE_URL}zones`,
    users: `${USER_BASE_URL}users`,
    requests: `${USER_BASE_URL}users/approvals`,
    approval: `${USER_BASE_URL}user/approvals`,
    bulkApproval: `${USER_BASE_URL}users/approvals/bulk`,
    status: `${USER_BASE_URL}user/status`,
  },
  admin: {
    tickets: `${USER_BASE_URL}tickets`,
    exportTicketsExcel: `${USER_BASE_URL}tickets/export/excel`,
    zones: `${USER_BASE_URL}zones`,
    deleteZone: (zoneId: string) =>
      `${USER_BASE_URL}zones/${encodeURIComponent(zoneId)}`,
    countryMapping: `${USER_BASE_URL}countries/zone-mapping`,
    zoneMapping: `${USER_BASE_URL}zones/country-mapping`,
    countries: `${USER_BASE_URL}countries`,
    updateCountry: (name: string) =>
      `${USER_BASE_URL}countries/${encodeURIComponent(name)}`,
    deleteCountry: (name: string) =>
      `${USER_BASE_URL}countries/${encodeURIComponent(name)}`,
    adminRequests: `${USER_BASE_URL}admin/requests`,
    updateTicketStatus: (ticketId: string, status: string) =>
      `${USER_BASE_URL}tickets/${encodeURIComponent(ticketId)}/status/${encodeURIComponent(status)}`,
    updateRequest: (requestId: string) =>
      `${USER_BASE_URL}admin/requests/${requestId}`,
    userRequests: `${USER_BASE_URL}users/requests`,
    users: `${USER_BASE_URL}admin/users`,
    onboardUser: `${USER_BASE_URL}users/onboard`,
    addUser: `${USER_BASE_URL}admin/user/`,
    searchEntraUser: `${USER_BASE_URL}users/search-entra-user`,
    countriesByZones: `${USER_BASE_URL}countries/zone/list`,
    updateUser: (userId: string) => `${USER_BASE_URL}admin/users/${userId}`,
    deactivateUser: (userId: string) =>
      `${USER_BASE_URL}admin/users/${userId}/deactivate`,
    requestNewZone: (zoneId: string) => `${USER_BASE_URL}users/zones/${zoneId}`,
  },
  experiments: {
    getExperiment: `${BASE_URL}`,
    getExperimentV1: `${BASE_URL}`,
    getExperiments: `${BASE_URL}experiments`,
    getExperimentsV3: `${BASE_URL}experiments`,
    dbconnectV1: `${BASE_URL}connect_db`,
    dbStatusV1: `${BASE_URL}db/status`,
    dbconnectV2: `${BASE_URL}connect_db`,
    dbStatusV2: `${BASE_URL}db/status`,
    createExperiment: `${BASE_URL}experiments`,
    uploadFile: `${BASE_URL}upload_file_experiment`,
    uploadFileV2: `${BASE_URL}/experiments/:experiment_id/file/:category`,
    columnMapping: `${BASE_URL}column_mapping`,
    validation: `${BASE_URL}validation`,
    delete: `${BASE_URL}experiments`,
    getprocess: `${EXPURL}process_by_experiment`,
    individualProcess: `${EXPURL}process`,
    download: `${EXPURL}download`,
    beforeEda: `${BASE_URL}standardize_column_names`,
    upload: `${EXPURL}upload`,
    updateState: `${EXPURL}process/state_name/`,
    states: `${EXPURL}states`,
    goProcess: `${GO_PROCESS_URL}`,
    goProcessStates: `${GOPROCESSSTATES}`,
    taskStream: (taskId: string) => `${PHO_TASK_STREAM_URL}${taskId}/status`,

    phoExperimentsFileUpload: `${PHOBASEURL}/experiments`,
    dtpFinancialImpact: (expId: string) =>
      `${PHOBASEURL}/${expId}/dtp/financial_impact`,
    checkDataAvailability: (expId: string, country: string) =>
      appendHttpParams(
        `${PHOBASEURL}/${expId}/dtp/data-availability`,
        createHttpParams({ country }),
      ),
    getPHOPlans: `${BASE_URL}pins`,
    validateDTPPlan: (expId: string, country: string, category: string) =>
      appendHttpParams(
        `${PHOBASEURL}/${expId}/dtp/validate-linked-experiment`,
        createHttpParams({ category, country }),
      ),
    saveDTPFile: (expId: string) =>
      `${PHOBASEURL}/${expId}/dtp/save-global-index`,
    submitDTPData: (expId: string) => `${PHOBASEURL}/${expId}/dtp/aggregate`,
    getVILCTable: (expId: string, flag: boolean) =>
      appendHttpParams(
        `${PHOBASEURL}/${expId}/dtp/sku-details`,
        createHttpParams({ filter_flag: flag }),
      ),
    updateVILCForSKU: (expId: string) =>
      `${PHOBASEURL}/${expId}/dtp/sku-details`,
    getHistoryLogs: (expId: string, skuId: string) =>
      appendHttpParams(
        `${PHOBASEURL}/${expId}/dtp/sku-audit-log`,
        createHttpParams({ sku: skuId }),
      ),
    getSimulationForDTP: (expId: string) =>
      `${PHOBASEURL}/${expId}/dtp/simulation`,
    refreshDTPData: (expId: string) => `${PHOBASEURL}/${expId}/dtp/refresh`,
    getLogisticsData: (expId: string, granularityLevel: string) =>
      appendHttpParams(
        `${PHOBASEURL}/${expId}/logistics`,
        createHttpParams({ granularity_level: granularityLevel }),
      ),
    getLogisticsDataV2: (expId: string) =>
      `${PHOBASEURL_V2}/${expId}/logistics`,
    postLogisticsData: (expId: string) => `${PHOBASEURL}/${expId}/logistics`,
    postLogisticsDataV2: (expId: string) =>
      `${PHOBASEURL_V2}/${expId}/logistics`,
    phoExperiments: `${PHOBASEURL}/experiments`,
    phoDTPExperiments: `dtp/experiments`,
    phoBaseUrl: `${PHOBASEURL}`,
    postLevel0Pareto: (expId: string) => `${PHOBASEURL}/${expId}/level0/pareto`,
    postLevel0ParetoV2: (expId: string) =>
      `${PHOBASEURL_V2}/${expId}/level0/pareto`,
    getLevel0Pareto: (expId: string, granularityLevel: string) =>
      appendHttpParams(
        `${PHOBASEURL}/${expId}/level0/pareto`,
        createHttpParams({ granularity_level: granularityLevel }),
      ),
    getLevel0ParetoV2: (expId: string) =>
      `${PHOBASEURL_V2}/${expId}/level0/pareto`,
    level0Refresh: (expId: string) => `${PHOBASEURL}/${expId}/level0/refresh`,
    level0ParetoRefresh: (expId: string) =>
      `${PHOBASEURL}/${expId}/level0/pareto/refresh`,
    nestingHistoricalDataAvailability: (expId: string) =>
      `${PHOBASEURL_V2}/${expId}/nesting/historical-data-availability`,
    postNestingCall: (expId: string, isOlderExperiment: boolean) =>
      `${currentPhoUrl(isOlderExperiment)}/${expId}/nesting/nesting`,
    refreshNestingValidation: (expId: string) =>
      `${PHOBASEURL_V2}/${expId}/nesting/refresh-validation`,
    logisticFrameWorkOutput: (expId: string) =>
      `${PHOBASEURL_V2}/${expId}/nesting/logistic-framework-output`,
    postNestingCallV2: (expId: string) =>
      `${PHOBASEURL_V2}/${expId}/nesting/nesting`,
    updateNestStatusV2: (expId: string) =>
      `${PHOBASEURL_V2}/${expId}/nesting/update-status`,
    nestingRefresh: (expId: string, isOlderExperiment: boolean) =>
      `${currentPhoUrl(isOlderExperiment)}/${expId}/nesting/refresh-nesting`,
    getSubLevelsV2: (expId: string, granularityLevel: string) =>
      appendHttpParams(
        `${PHOBASEURL_V2}/${expId}/nesting/sub-granularities`,
        createHttpParams({ granularity_level: granularityLevel }),
      ),
    financialImpactProcessCall: (expId: string) =>
      `${PHOBASEURL}/${expId}/financial-impact/process-mapping`,
    financialImpactCall: (expId: string, isOlderExperiment: boolean) =>
      `${currentPhoUrl(isOlderExperiment)}/${expId}/financial-impact`,
    financialImpactRefresh: (expId: string) =>
      `${PHOBASEURL_V3}/${expId}/financial-impact/refresh`,
    getFinancialImpactStatusCall: (expId: string) =>
      `${PHOBASEURL}/experiments/${expId}/has-sellout-data`,
    refreshMappingTable: (expId: string) =>
      `${PHOBASEURL}/${expId}/financial-impact/refresh-mapping`,
    runDTPValidation: (expId: string, type: string) =>
      `${PHOBASEURL}/${expId}/dtp/validate/file/${type}`,
    runFIValidation: (expId: string) =>
      `${PHOBASEURL}/${expId}/financial-impact/validate`,
    runFIValidationWithType: (expId: string, type: string) =>
      `${PHOBASEURL}/${expId}/financial-impact/validate/${type}`,
    getSimulationSubLevelsV3: (expId: string, granularityLevel: string) =>
      appendHttpParams(
        `${PHOBASEURL_V3}/${expId}/simulation/sub-granularities`,
        createHttpParams({ granularity_level: granularityLevel }),
      ),
    getSimulationResult: (expId: string, version: string) =>
      `${phoVersionUrl(version)}/${expId}/simulation/result`,
    getIcDataPipeline: (expId: string) =>
      `${PHOBASEURL_V3}/${expId}/simulation/ic-data-pipeline`,
    getSimulationRefresh: (expId: string, version: string) =>
      `${phoVersionUrl(version)}/${expId}/simulation/refresh-simulation`,
    getSimulationPHIData: (expId: string) =>
      `${PHOBASEURL_V2}/${expId}/simulation/extraction_report`,
    postSimulation: (expId: string) => `${PHOBASEURL_V2}/${expId}/simulation`,
    getSkuDetails: (expId: string, skuId: string, isV3Experiment: boolean) =>
      appendHttpParams(
        `${phoVersionUrl(isV3Experiment ? "V3" : "V1")}/${expId}/simulation/sku-details`,
        createHttpParams({ sku_id: skuId }),
      ),
    getSimulationSummary: (expId: string, version: string) =>
      `${phoVersionUrl(version)}/${expId}/simulation/simulation-summary`,
    getThreshold: (expId: string, isOlderExperiment: boolean) =>
      `${currentPhoUrl(isOlderExperiment)}/${expId}/simulation/minimum-threshold`,
    createOptimization: (expId: string, isOlderExperiment: boolean) =>
      `${currentPhoUrl(isOlderExperiment)}/${expId}/optimization/pygad`,
    updateNestingSelection: (expId: string) =>
      `${PHOBASEURL_V2}/${expId}/nesting/update-selection`,
    updateSimulation: (expId: string, version: string) =>
      `${phoVersionUrl(version)}/${expId}/simulation/update-output`,
    simulationReport: (expId: string) =>
      `${PHOBASEURL}/${expId}/simulation/report`,
    dtpFinancial: (expId: string) =>
      `${PHOBASEURL_V2}/${expId}/financial-impact/dtp-financial-impact`,
    financialImpactReset: (expId: string) =>
      `${PHOBASEURL}/${expId}/financial-impact/reset/financial_impact`,
    simulationUpdateResult: (expId: string) =>
      `${PHOBASEURL}/${expId}/simulation/update-result`,
    votingSummary: (expId: string) => `${PHOBASEURL}/${expId}/voting/summary`,
    votingDepartments: (expId: string) =>
      `${PHOBASEURL}/${expId}/voting/departments`,
    initiateVoting: (expId: string) => `${PHOBASEURL}/${expId}/voting/voting`,
    votingUsers: (expId: string) => `${PHOBASEURL}/${expId}/voting/users`,
    addUser: (expId: string) => `${PHOBASEURL}/${expId}/voting/users`,
    resetUser: (expId: string) => `${PHOBASEURL}/${expId}/voting/users/reset`,
    sendNotification: (expId: string) =>
      `${PHOBASEURL}/${expId}/voting/users/remind`,
    closeVoting: (expId: string) =>
      `${PHOBASEURL}/${expId}/voting/close-voting`,
    updateVoting: (expId: string) => `${PHOBASEURL}/${expId}/voting/voting`,
    submitVoting: (expId: string) =>
      `${PHOBASEURL}/${expId}/voting/vote/submit`,
    saveVoting: (expId: string, isOlderExperiment: boolean) =>
      `${currentPhoUrl(isOlderExperiment)}/${expId}/voting/vote/save`,
    simulationResult: (expId: string, isOlderExperiment: boolean) =>
      `${currentPhoUrl(isOlderExperiment)}/${expId}/voting/vote/simulation`,
    skuVoting: (expId: string, voterId: string) =>
      appendHttpParams(
        `${PHOBASEURL}/${expId}/voting/vote/skus`,
        createHttpParams({ voter_id: voterId }),
      ),
    skuForSpr: (expId: string) => `${PHOBASEURL}/${expId}/spr/sku-for-spr`,
    innovationData: (expId: string) =>
      `${PHOBASEURL}/${expId}/spr/ppr_innovation_sku`,
    phiDetailsForSpr: (expId: string) =>
      `${PHOBASEURL_V2}/${expId}/spr/phi_details`,
    saveSprDecision: (expId: string) =>
      `${PHOBASEURL}/${expId}/spr/save-spr-decision`,
    concludeSpr: (expId: string) => `${PHOBASEURL}/${expId}/spr/conclude-spr`,
    summarySimulate: (expId: string, isOlderExperiment: boolean) =>
      `${currentPhoUrl(isOlderExperiment)}/${expId}/summary/simulate`,
    summaryPublish: (expId: string, isOlderExperiment: boolean) =>
      `${currentPhoUrl(isOlderExperiment)}/${expId}/summary/publish`,
    skuJourneyData: (expId: string) =>
      `${PHOBASEURL}/${expId}/summary/sku_journey`,
    sprSkuJourneyData: (expId: string) =>
      `${PHOBASEURL}/${expId}/spr/sku_journey`,
    excelDownload: (expId: string) => `${PHOBASEURL}/${expId}/summary/download`,
    skuDetailsDownload: (expId: string) =>
      `${PHOBASEURL}/${expId}/dashboard/report`,
    globalDashboardReport: (expId: string) =>
      `${PHOBASEURL}/${expId}/dashboard/extract_report`,
    shareExecutiveSummary: (expId: string) =>
      `${PHOBASEURL}/${expId}/summary/share`,
    sharePprSummary: (expId: string) => `${PHOBASEURL}/${expId}/spr/share`,
    phoSearchUser: `${USER_BASE_URL}users/search-entra-user`,
    downloadSprReport: (expId: string) => `${PHOBASEURL}/${expId}/spr/download`,
    financialImpactReport: (expId: string, isOlderExperiment: boolean) =>
      `${currentPhoUrl(isOlderExperiment)}/${expId}/financial-impact/download-report`,
    summarySellout: (expId: string) => `${PHOBASEURL}/${expId}/summary/sellout`,
    kpisAPI: (expId: string, isOlderExperiment: boolean) =>
      `${currentPhoUrl(isOlderExperiment)}/${expId}/spr/kpis`,
    sprSummary: (expId: string) => `${PHOBASEURL}/${expId}/spr/summary`,
    previousAnalysis: (expId: string) =>
      `${PHOBASEURL}/experiments/${expId}/previous-analysis`,
    summaryConclude: (expId: string, version: string) =>
      `${phoVersionUrl(version, "V1")}/${expId}/summary/conclude`,
    skuJourneyUpload: (expId: string) =>
      `${PHOBASEURL}/${expId}/summary/sku_journey/upload`,
    skuJourneyRefresh: (expId: string) =>
      `${PHOBASEURL}/${expId}/summary/sku_journey/refresh`,
    pprSkuJourneyRefresh: (expId: string) =>
      `${PHOBASEURL}/${expId}/spr/sku_journey/refresh`,

    affectedDashboard: (expId: string) =>
      `${PHOBASEURL}/${expId}/dashboard/affected-dashboard`,
  },
  dashboard: {
    getfilters: (expId: string) =>
      `${PHOBASEURL}/${expId}/dashboard/exp-details`,
    getSKUDetailsTablePagination: (
      expId: string,
      pageNumber: number,
      perPage: number,
    ) =>
      appendHttpParams(
        `${PHOBASEURL}/${expId}/dashboard/sku-details`,
        createHttpParams({ page_number: pageNumber, per_page: perPage }),
      ),
    getFilterResult: (expId: string, isOlderExperiment: boolean) =>
      `${currentPhoUrl(isOlderExperiment)}/${expId}/dashboard/filter-result`,
    getDashboard: (expId: string, isOlderExperiment: boolean) =>
      `${currentPhoUrl(isOlderExperiment)}/${expId}/dashboard/result`,
  },
};
